import { useState } from "react";
import { MonthYearPicker } from "@/components/MonthYearPicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, GripVertical, Briefcase, GraduationCap, User, Star, FolderPlus, ArrowUp, ArrowDown } from "lucide-react";
import { ResumeData, ExperienceItem, EducationItem, SkillCategory, CustomSection, CustomSectionItem } from "@/types/resume";
import { createEmptyExperience, createEmptyEducation, createEmptySkillCategory, createEmptyCustomSection, createEmptyCustomSectionItem, createEmptyBulletItem } from "@/lib/resumeUtils";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LinkInputWithDropdown } from "@/components/LinkInputWithDropdown";
import { SavedLinksManager } from "@/components/SavedLinksManager";
import { SectionVisibilityControls, SectionVisibility, defaultSectionVisibility } from "@/components/SectionVisibilityControls";
import { DraggableSectionTabs, SectionKey, DEFAULT_SECTION_ORDER } from "@/components/DraggableSectionTabs";

interface ResumeFormProps {
  data: ResumeData;
  onChange: (data: ResumeData) => void;
  sectionVisibility: SectionVisibility;
  onSectionVisibilityChange: (visibility: SectionVisibility) => void;
  sectionOrder: SectionKey[];
  onSectionOrderChange: (order: SectionKey[]) => void;
}

export const ResumeForm = ({ data, onChange, sectionVisibility, onSectionVisibilityChange, sectionOrder, onSectionOrderChange }: ResumeFormProps) => {
  // Ensure arrays are always defined to prevent .map() crashes
  const experience = data.experience || [];
  const education = data.education || [];
  const coreStrengths = data.coreStrengths || [];
  const customSections = data.customSections || [];

  const swapItems = <T,>(items: T[], a: number, b: number) => {
    const next = [...items];
    const temp = next[a];
    next[a] = next[b];
    next[b] = temp;
    return next;
  };

  const updatePersonalInfo = (field: keyof ResumeData["personalInfo"], value: string) => {
    onChange({
      ...data,
      personalInfo: { ...data.personalInfo, [field]: value },
    });
  };

  // Experience handlers
  const addExperience = () => {
    onChange({
      ...data,
      experience: [...experience, createEmptyExperience()],
    });
  };

  const updateExperience = (id: string, updates: Partial<ExperienceItem>) => {
    onChange({
      ...data,
      experience: experience.map((exp) =>
        exp.id === id ? { ...exp, ...updates } : exp
      ),
    });
  };

  const removeExperience = (id: string) => {
    onChange({
      ...data,
      experience: experience.filter((exp) => exp.id !== id),
    });
  };

  const moveExperience = (id: string, direction: -1 | 1) => {
    const idx = experience.findIndex((e) => e.id === id);
    if (idx < 0) return;
    const nextIdx = idx + direction;
    if (nextIdx < 0 || nextIdx >= experience.length) return;
    onChange({
      ...data,
      experience: swapItems(experience, idx, nextIdx),
    });
  };

  const addBullet = (expId: string) => {
    const exp = experience.find((e) => e.id === expId);
    if (exp) {
      updateExperience(expId, { bullets: [...exp.bullets, ""] });
    }
  };

  const updateBullet = (expId: string, bulletIndex: number, value: string) => {
    const exp = experience.find((e) => e.id === expId);
    if (exp) {
      const newBullets = [...exp.bullets];
      newBullets[bulletIndex] = value;
      updateExperience(expId, { bullets: newBullets });
    }
  };

  const removeBullet = (expId: string, bulletIndex: number) => {
    const exp = experience.find((e) => e.id === expId);
    if (exp && exp.bullets.length > 1) {
      updateExperience(expId, {
        bullets: exp.bullets.filter((_, idx) => idx !== bulletIndex),
      });
    }
  };

  // Education handlers
  const addEducation = () => {
    onChange({
      ...data,
      education: [...education, createEmptyEducation()],
    });
  };

  const updateEducation = (id: string, updates: Partial<EducationItem>) => {
    onChange({
      ...data,
      education: education.map((edu) =>
        edu.id === id ? { ...edu, ...updates } : edu
      ),
    });
  };

  const removeEducation = (id: string) => {
    onChange({
      ...data,
      education: education.filter((edu) => edu.id !== id),
    });
  };

  // Core Strengths handlers
  const addSkillCategory = () => {
    onChange({
      ...data,
      coreStrengths: [...coreStrengths, createEmptySkillCategory()],
    });
  };

  const updateSkillCategory = (id: string, updates: Partial<SkillCategory>) => {
    onChange({
      ...data,
      coreStrengths: coreStrengths.map((cat) =>
        cat.id === id ? { ...cat, ...updates } : cat
      ),
    });
  };

  const removeSkillCategory = (id: string) => {
    onChange({
      ...data,
      coreStrengths: coreStrengths.filter((cat) => cat.id !== id),
    });
  };

  // Custom Section handlers
  const addCustomSection = () => {
    onChange({
      ...data,
      customSections: [...customSections, createEmptyCustomSection()],
    });
  };

  const updateCustomSection = (id: string, updates: Partial<CustomSection>) => {
    onChange({
      ...data,
      customSections: customSections.map((section) =>
        section.id === id ? { ...section, ...updates } : section
      ),
    });
  };

  const removeCustomSection = (id: string) => {
    onChange({
      ...data,
      customSections: customSections.filter((section) => section.id !== id),
    });
  };

  const moveCustomSection = (id: string, direction: -1 | 1) => {
    const idx = customSections.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const nextIdx = idx + direction;
    if (nextIdx < 0 || nextIdx >= customSections.length) return;
    onChange({
      ...data,
      customSections: swapItems(customSections, idx, nextIdx),
    });
  };

  const addCustomSectionItem = (sectionId: string) => {
    const section = customSections.find((s) => s.id === sectionId);
    if (section) {
      updateCustomSection(sectionId, { items: [...(section.items || []), createEmptyCustomSectionItem()] });
    }
  };

  const addCustomSectionBulletItem = (sectionId: string) => {
    const section = customSections.find((s) => s.id === sectionId);
    if (section) {
      updateCustomSection(sectionId, { items: [...(section.items || []), createEmptyBulletItem()] });
    }
  };

  const updateCustomSectionItem = (sectionId: string, itemId: string, updates: Partial<CustomSectionItem>) => {
    const section = customSections.find((s) => s.id === sectionId);
    if (section) {
      updateCustomSection(sectionId, {
        items: (section.items || []).map((item) =>
          item.id === itemId ? { ...item, ...updates } : item
        ),
      });
    }
  };

  const removeCustomSectionItem = (sectionId: string, itemId: string) => {
    const section = customSections.find((s) => s.id === sectionId);
    if (section) {
      updateCustomSection(sectionId, {
        items: (section.items || []).filter((item) => item.id !== itemId),
      });
    }
  };

  const moveCustomSectionItem = (sectionId: string, itemId: string, direction: -1 | 1) => {
    const section = customSections.find((s) => s.id === sectionId);
    if (!section) return;
    const items = section.items || [];
    const idx = items.findIndex((i) => i.id === itemId);
    if (idx < 0) return;
    const nextIdx = idx + direction;
    if (nextIdx < 0 || nextIdx >= items.length) return;
    updateCustomSection(sectionId, {
      items: swapItems(items, idx, nextIdx),
    });
  };

  const addCustomItemBullet = (sectionId: string, itemId: string) => {
    const section = customSections.find((s) => s.id === sectionId);
    const item = section?.items?.find((i) => i.id === itemId);
    if (item) {
      updateCustomSectionItem(sectionId, itemId, { bullets: [...(item.bullets || []), ""] });
    }
  };

  const updateCustomItemBullet = (sectionId: string, itemId: string, bulletIndex: number, value: string) => {
    const section = customSections.find((s) => s.id === sectionId);
    const item = section?.items?.find((i) => i.id === itemId);
    if (item) {
      const newBullets = [...(item.bullets || [])];
      newBullets[bulletIndex] = value;
      updateCustomSectionItem(sectionId, itemId, { bullets: newBullets });
    }
  };

  const removeCustomItemBullet = (sectionId: string, itemId: string, bulletIndex: number) => {
    const section = customSections.find((s) => s.id === sectionId);
    const item = section?.items?.find((i) => i.id === itemId);
    if (item && (item.bullets || []).length > 1) {
      updateCustomSectionItem(sectionId, itemId, {
        bullets: (item.bullets || []).filter((_, idx) => idx !== bulletIndex),
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls Row */}
      <div className="flex items-center gap-2 flex-wrap" data-tour="section-controls">
        <SectionVisibilityControls
          visibility={sectionVisibility}
          onChange={onSectionVisibilityChange}
        />
        <SavedLinksManager />
      </div>

      <Tabs defaultValue="personal" className="w-full">
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm pb-2 -mx-1 px-1" data-tour="form-tabs">
          <DraggableSectionTabs
            sectionOrder={sectionOrder}
            onOrderChange={onSectionOrderChange}
            sectionVisibility={sectionVisibility}
          />
          <p className="text-[10px] text-muted-foreground mt-1 text-center">
            Drag tabs to reorder sections
          </p>
        </div>

        <TabsContent value="personal" className="space-y-4 animate-fade-in">
          <Card className="p-5 glass-effect">
            <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
              <User className="w-4 h-4 text-primary" />
              Contact Information
            </h3>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  placeholder="John Doe"
                  value={data.personalInfo.fullName}
                  onChange={(e) => updatePersonalInfo("fullName", e.target.value)}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john.doe@example.com"
                    value={data.personalInfo.email}
                    onChange={(e) => updatePersonalInfo("email", e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    placeholder="(555) 123-4567"
                    value={data.personalInfo.phone}
                    onChange={(e) => updatePersonalInfo("phone", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  placeholder="City, State"
                  value={data.personalInfo.location}
                  onChange={(e) => updatePersonalInfo("location", e.target.value)}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="github">GitHub Link (optional)</Label>
                  <LinkInputWithDropdown
                    id="github"
                    placeholder="github.com/johndoe"
                    value={data.personalInfo.github || ""}
                    onChange={(value) => updatePersonalInfo("github", value)}
                    linkType="github"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="portfolio">Portfolio (optional)</Label>
                  <LinkInputWithDropdown
                    id="portfolio"
                    placeholder="johndoe.com"
                    value={data.personalInfo.portfolio || ""}
                    onChange={(value) => updatePersonalInfo("portfolio", value)}
                    linkType="portfolio"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="linkedin">LinkedIn (optional)</Label>
                <LinkInputWithDropdown
                  id="linkedin"
                  placeholder="linkedin.com/in/johndoe"
                  value={data.personalInfo.linkedin || ""}
                  onChange={(value) => updatePersonalInfo("linkedin", value)}
                  linkType="linkedin"
                />
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="summary" className="animate-fade-in">
          <Card className="p-5 glass-effect">
            <h3 className="font-heading font-semibold mb-4">Professional Summary</h3>
            <Textarea
              placeholder="Results-driven professional with 5+ years of experience in [your field]. Proven track record in [key skills]. Passionate about [industry/goal] and committed to delivering measurable results..."
              value={data.summary}
              onChange={(e) => onChange({ ...data, summary: e.target.value })}
              rows={6}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Write 2-4 sentences highlighting your key qualifications, technical skills, and what you're known for.
            </p>
          </Card>
        </TabsContent>

        <TabsContent value="skills" className="space-y-4 animate-fade-in">
          <Card className="p-5 glass-effect">
            <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
              <Star className="w-4 h-4 text-primary" />
              Skills
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Organize your skills by category (e.g., "Front-End", "Testing", "Networking/Systems")
            </p>

            {coreStrengths.map((cat, index) => (
              <div key={cat.id} className="mb-4 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium">Category {index + 1}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeSkillCategory(cat.id)}
                    className="text-destructive hover:text-destructive h-8 w-8"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid gap-3">
                  <div className="grid gap-2">
                    <Label>Category Name</Label>
                    <Input
                      placeholder="Front-End"
                      value={cat.category}
                      onChange={(e) => updateSkillCategory(cat.id, { category: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Skills (comma-separated)</Label>
                    <Input
                      placeholder="HTML, CSS, JavaScript, TypeScript, React"
                      value={cat.skills}
                      onChange={(e) => updateSkillCategory(cat.id, { skills: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button onClick={addSkillCategory} variant="outline" className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Add Skill Category
            </Button>
          </Card>
        </TabsContent>

        <TabsContent value="experience" className="space-y-4 animate-fade-in">
          {experience.map((exp, index) => (
            <Card key={exp.id} className="p-5 glass-effect">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                  <h3 className="font-heading font-semibold">Position {index + 1}</h3>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => moveExperience(exp.id, -1)}
                    disabled={index === 0}
                    aria-label="Move experience up"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => moveExperience(exp.id, 1)}
                    disabled={index === experience.length - 1}
                    aria-label="Move experience down"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeExperience(exp.id)}
                    className="text-destructive hover:text-destructive"
                    aria-label="Delete experience"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Company *</Label>
                    <Input
                      placeholder="Acme Corporation"
                      value={exp.company}
                      onChange={(e) => updateExperience(exp.id, { company: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Job Title *</Label>
                    <Input
                      placeholder="Software Engineer"
                      value={exp.jobTitle}
                      onChange={(e) => updateExperience(exp.id, { jobTitle: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Location</Label>
                    <Input
                      placeholder="Chicago, IL"
                      value={exp.location}
                      onChange={(e) => updateExperience(exp.id, { location: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Work Type</Label>
                    <Select
                      value={exp.workType || ""}
                      onValueChange={(value) => updateExperience(exp.id, { workType: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Remote">Remote</SelectItem>
                        <SelectItem value="Hybrid/Remote">Hybrid/Remote</SelectItem>
                        <SelectItem value="On-site">On-site</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Start Date</Label>
                    <MonthYearPicker
                      value={exp.startDate}
                      onChange={(newStartDate) => {
                        if (exp.endDate && newStartDate > exp.endDate) {
                          updateExperience(exp.id, { startDate: newStartDate, endDate: "" });
                        } else {
                          updateExperience(exp.id, { startDate: newStartDate });
                        }
                      }}
                      placeholder="Start date"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>End Date</Label>
                    <MonthYearPicker
                      value={exp.endDate}
                      onChange={(newEndDate) => {
                        if (exp.startDate && newEndDate && newEndDate < exp.startDate) return;
                        updateExperience(exp.id, { endDate: newEndDate });
                      }}
                      placeholder="End date"
                      disabled={exp.current}
                    />
                    {exp.startDate && exp.endDate && exp.endDate < exp.startDate && !exp.current && (
                      <p className="text-xs text-destructive">End date cannot be before start date</p>
                    )}
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`current-${exp.id}`}
                        checked={exp.current}
                        onCheckedChange={(checked) =>
                          updateExperience(exp.id, { current: checked as boolean })
                        }
                      />
                      <Label htmlFor={`current-${exp.id}`} className="text-xs">
                        Current position
                      </Label>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Accomplishments (bullet points) *</Label>
                  {exp.bullets.map((bullet, bulletIndex) => (
                    <div key={bulletIndex} className="flex gap-2">
                      <Textarea
                        placeholder="Built and maintained client-facing web experiences in WordPress, implementing responsive UI sections and interactive components focused on stability and clear UX."
                        value={bullet}
                        onChange={(e) => updateBullet(exp.id, bulletIndex, e.target.value)}
                        rows={2}
                        className="resize-none flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeBullet(exp.id, bulletIndex)}
                        disabled={exp.bullets.length <= 1}
                        className="shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addBullet(exp.id)}
                    className="mt-2"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Bullet Point
                  </Button>
                </div>
              </div>
            </Card>
          ))}

          <Button onClick={addExperience} variant="outline" className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Add Experience
          </Button>
        </TabsContent>

        <TabsContent value="education" className="space-y-4 animate-fade-in">
          {education.map((edu, index) => (
            <Card key={edu.id} className="p-5 glass-effect">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                  <h3 className="font-heading font-semibold">Education {index + 1}</h3>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeEducation(edu.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label>Institution *</Label>
                  <Input
                    placeholder="State University"
                    value={edu.institution}
                    onChange={(e) => updateEducation(edu.id, { institution: e.target.value })}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Degree *</Label>
                    <Input
                      placeholder="Bachelor of Science"
                      value={edu.degree}
                      onChange={(e) => updateEducation(edu.id, { degree: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Field of Study</Label>
                    <Input
                      placeholder="Computer Science"
                      value={edu.field || ""}
                      onChange={(e) => updateEducation(edu.id, { field: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label>Location</Label>
                    <Input
                      placeholder="City, State"
                      value={edu.location}
                      onChange={(e) => updateEducation(edu.id, { location: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Graduation Year</Label>
                    <MonthYearPicker
                      value={edu.graduationDate}
                      onChange={(val) => updateEducation(edu.id, { graduationDate: val })}
                      placeholder="Graduation date"
                      allowYearOnly
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>GPA (optional)</Label>
                    <Input
                      placeholder="3.8"
                      value={edu.gpa || ""}
                      onChange={(e) => updateEducation(edu.id, { gpa: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </Card>
          ))}

          <Button onClick={addEducation} variant="outline" className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Add Education
          </Button>
        </TabsContent>

        <TabsContent value="custom" className="space-y-4 animate-fade-in">
          <Card className="p-5 glass-effect">
            <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
              <FolderPlus className="w-4 h-4 text-primary" />
              Custom Sections
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Add custom sections like Certifications, Volunteering, Projects, Publications, Awards, etc.
            </p>

            {customSections.map((section, sectionIndex) => (
              <div key={section.id} className="mb-6 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 flex-1 mr-4">
                    <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                    <Input
                      placeholder="Section Title (e.g., Certifications)"
                      value={section.title}
                      onChange={(e) => updateCustomSection(section.id, { title: e.target.value })}
                      className="font-semibold"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => moveCustomSection(section.id, -1)}
                      disabled={sectionIndex === 0}
                      className="h-8 w-8"
                      aria-label="Move section up"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => moveCustomSection(section.id, 1)}
                      disabled={sectionIndex === customSections.length - 1}
                      className="h-8 w-8"
                      aria-label="Move section down"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeCustomSection(section.id)}
                      className="text-destructive hover:text-destructive h-8 w-8"
                      aria-label="Delete section"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {(section.items || []).map((item, itemIndex) => (
                  item.type === 'bullet' ? (
                    <div key={item.id} className="ml-4 mb-2 flex items-center gap-2">
                      <span className="text-muted-foreground text-sm select-none">•</span>
                      <Input
                        placeholder="e.g. Fluent in Spanish"
                        value={item.title}
                        onChange={(e) => updateCustomSectionItem(section.id, item.id, { title: e.target.value })}
                        className="flex-1 h-8 text-sm"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeCustomSectionItem(section.id, item.id)}
                        className="text-destructive hover:text-destructive h-8 w-8 shrink-0"
                        aria-label="Delete bullet"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                  <div key={item.id} className="ml-4 mb-4 p-3 bg-background/50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">Item {itemIndex + 1}</span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => moveCustomSectionItem(section.id, item.id, -1)}
                          disabled={itemIndex === 0}
                          className="h-7 w-7"
                          aria-label="Move item up"
                        >
                          <ArrowUp className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => moveCustomSectionItem(section.id, item.id, 1)}
                          disabled={itemIndex === (section.items || []).length - 1}
                          className="h-7 w-7"
                          aria-label="Move item down"
                        >
                          <ArrowDown className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeCustomSectionItem(section.id, item.id)}
                          className="text-destructive hover:text-destructive h-7 w-7"
                          aria-label="Delete item"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid gap-3">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="grid gap-2">
                          <Label className="text-xs">Title</Label>
                          <Input
                            placeholder="AWS Solutions Architect"
                            value={item.title}
                            onChange={(e) => updateCustomSectionItem(section.id, item.id, { title: e.target.value })}
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label className="text-xs">Date (optional)</Label>
                          <Input
                            placeholder="2024"
                            value={item.date || ""}
                            onChange={(e) => updateCustomSectionItem(section.id, item.id, { date: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="grid gap-2">
                          <Label className="text-xs">Subtitle (optional)</Label>
                          <Input
                            placeholder="Amazon Web Services"
                            value={item.subtitle || ""}
                            onChange={(e) => updateCustomSectionItem(section.id, item.id, { subtitle: e.target.value })}
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label className="text-xs">Project Link (optional)</Label>
                          <LinkInputWithDropdown
                            placeholder="https://github.com/user/project"
                            value={item.link || ""}
                            onChange={(value) => updateCustomSectionItem(section.id, item.id, { link: value })}
                            linkType="project"
                          />
                        </div>
                      </div>
                      <div className="grid gap-2">
                        <Label className="text-xs">Description (optional)</Label>
                        <Textarea
                          placeholder="Brief description..."
                          value={item.description || ""}
                          onChange={(e) => updateCustomSectionItem(section.id, item.id, { description: e.target.value })}
                          rows={2}
                          className="resize-none"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Bullet Points (optional)</Label>
                        {(item.bullets || []).map((bullet, bulletIndex) => (
                          <div key={bulletIndex} className="flex gap-2">
                            <Input
                              placeholder="Achievement or detail..."
                              value={bullet}
                              onChange={(e) => updateCustomItemBullet(section.id, item.id, bulletIndex, e.target.value)}
                              className="flex-1"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeCustomItemBullet(section.id, item.id, bulletIndex)}
                              disabled={(item.bullets || []).length <= 1}
                              className="shrink-0 h-9 w-9"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => addCustomItemBullet(section.id, item.id)}
                          className="text-xs"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Bullet
                        </Button>
                      </div>
                    </div>
                  </div>
                  )
                ))}

                <div className="ml-4 flex gap-2 mt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addCustomSectionItem(section.id)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Item
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addCustomSectionBulletItem(section.id)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Bullet
                  </Button>
                </div>
              </div>
            ))}

            <Button onClick={addCustomSection} variant="outline" className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Add Custom Section
            </Button>
          </Card>
        </TabsContent>
      </Tabs>

    </div>
  );
};
